import React from "react";
import ConfirmationModal from "../ConfirmationModal";
import ConfirmationDialog from "../ConfirmationDialog";
import SaveTemplateModal from "../SaveTemplateModal";
import LoadTemplateModal from "../LoadTemplateModal";
import UpdateTemplateConfirmationModal from "../UpdateTemplateConfirmationModal";
import AttachmentModal from "../AttachmentModal";
import AttachmentPreviewModal from "../AttachmentPreviewModal";
import TestSendModal from "../TestSendModal";
import ScheduleModal from "../ScheduleModal";

/**
 * Groups the composer's modal mounts so the root component stays readable.
 *
 * Purely a mounting point — every modal's open state and handlers are owned by the
 * composer and passed straight through.
 */
function EmailSenderModals({ modals, body, attachmentsApi, schedule, templates, composer }) {
  const { attachments } = attachmentsApi;
  const isDrop = body.isDropEditor;

  return (
    <>
      <ConfirmationModal
        open={modals.confirm}
        onClose={() => {
          modals.setConfirm(false);
          schedule.setSendNowMode(false); // Reset Send Now mode when closing
          // Revert attachments to original if in edit mode and Cancel is clicked
          if (schedule.editMode) {
            attachmentsApi.revertToOriginal();
          }
        }}
        mailLists={composer.mailLists}
        selectedTo={composer.selectedTo}
        recipientMode={composer.recipientMode}
        adHocRecipients={composer.adHocRecipients}
        emailFrom={composer.emailFrom}
        replyTo={composer.replyTo}
        subject={composer.subject}
        emailBody={body.bodyHtml}
        attachments={attachments}
        scheduledDate={composer.scheduledDate}
        onEmailSent={composer.onEmailSent}
        contentText={isDrop ? "" : body.textContent}
        contentDrop={isDrop ? body.dropDesignJson : ""}
        editMode={schedule.editMode}
        editingScheduleId={schedule.editingScheduleId}
        sendNowMode={schedule.sendNowMode}
        scheduleId={schedule.currentScheduleId}
      />

      <SaveTemplateModal
        open={modals.saveTemplate}
        onClose={() => modals.setSaveTemplate(false)}
        editorType={body.mode}
        textContent={body.textContent}
        dropContent={body.dropDesignJson}
        renderedHtml={body.bodyHtml}
        onSaved={templates.trackSavedTemplate}
      />

      <UpdateTemplateConfirmationModal
        open={modals.updateTemplate}
        onClose={() => modals.setUpdateTemplate(false)}
        onConfirm={composer.onConfirmUpdateTemplate}
        templateName={templates.loadedTemplateName}
        updating={templates.updating}
      />

      <LoadTemplateModal
        open={modals.loadTemplate}
        onClose={() => modals.setLoadTemplate(false)}
        onLoadTemplate={(templateData) =>
          templates.applyLoadedTemplate(templateData, body.isDropEditor)
        }
      />

      <AttachmentModal
        open={modals.attachment}
        onClose={() => modals.setAttachment(false)}
        onConfirm={(base64Attachments) => {
          if (!schedule.editMode) {
            // Regular mode - append new attachments to existing ones
            attachmentsApi.appendAttachments(base64Attachments);
          }
        }}
        editMode={schedule.editMode && attachmentsApi.modalMode === "edit"}
        existingAttachments={attachments}
        onAttachmentsUpdated={(finalAttachments) => {
          // The modal returns the complete desired list, which is what gets sent in
          // the UpdateScheduledEmail call. Nothing else to track.
          attachmentsApi.setAttachments(finalAttachments);
        }}
      />

      <TestSendModal
        open={modals.testSend}
        onClose={() => modals.setTestSend(false)}
        emailFrom={composer.emailFrom}
        replyTo={composer.replyTo}
        subject={composer.subject}
        emailBody={body.bodyHtml}
        attachments={attachments}
      />

      <ConfirmationDialog
        open={modals.cancelEdits}
        onClose={() => modals.setCancelEdits(false)}
        onConfirm={composer.onEmailSent}
        title="Cancel Editing?"
        message="Are you sure you want to cancel editing? All unsaved changes will be lost."
        confirmText="Yes"
        cancelText="No"
        severity="warning"
        confirmButtonColor="error"
        confirmButtonVariant="contained"
      />

      <ScheduleModal
        open={modals.schedule}
        onClose={() => modals.setSchedule(false)}
        onScheduleSet={composer.onScheduleSet}
        onUndoSchedule={composer.onUndoSchedule}
        initialDate={schedule.editMode ? composer.scheduledDate : null}
      />

      <AttachmentPreviewModal
        open={modals.preview}
        onClose={() => modals.setPreview(false)}
        attachment={composer.previewAttachment}
      />
    </>
  );
}

export default EmailSenderModals;
